import { axiosInstance } from "@/lib/axios";
import {
    ProductResponse,
    SpecialOffer,
    Pack,
    SpecialOfferDetailResponse,
    SpecialOfferSingleResponse,
    PaginatedProductResponse
} from "@/types/product.types";

// 🔹 CREATE PACK
export const createPackApi = async (formData: FormData): Promise<SpecialOfferSingleResponse> => {
    const { data } = await axiosInstance.post("/pack-products", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return data;
};
// 🔹 PACKS LANDING
export const getPackProductsApi = async (params?: { page?: number; limit?: number }): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.get("/products/pack-products", {
        params,
    });
    return data;
};

// 🔹 PACKS SERVER FETCH (ISR)
export const getPackProductsServer = async (params?: { page?: number; limit?: number }): Promise<PaginatedProductResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());

    const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/v1/products/pack-products?${searchParams.toString()}`,
        { next: { revalidate: 60 } }
    );

    if (!res.ok) throw new Error("Fetch error");

    return res.json();
};


// 🔹 ADMIN SPECIAL OFFERS
export const getAdminSpecialOffersApi = async (): Promise<{ data: { message: SpecialOffer[] } }> => {
    return axiosInstance.get("/special-offer");
};

export const createSpecialOfferApi = async (formData: FormData): Promise<SpecialOfferSingleResponse> => {
    const { data } = await axiosInstance.post("/special-offer", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return data;
};

export const updateSpecialOfferApi = async (id: string, formData: FormData): Promise<SpecialOfferSingleResponse> => {
    const { data } = await axiosInstance.put(`/special-offer/${id}`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return data;
};

export const deleteSpecialOfferApi = async (id: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await axiosInstance.delete(`/special-offer/${id}`);
    return data;
};

export const updateOfferProductsApi = async (id: string, productsConfig: { productId: string; discountType?: string; discountValue?: number }[]): Promise<{ success: boolean }> => {
    const { data } = await axiosInstance.put(`/special-offer/${id}/products`, { productsConfig });
    return data;
};

//  POST CREATE PRODUCT
export const createProductApi = async (formData: FormData): Promise<ProductResponse> => {
    const { data } = await axiosInstance.post("/products", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return data;
};

export const toggleProductStatusApi = async (id: string): Promise<ProductResponse> => {
    const { data } = await axiosInstance.put(`/products/toggle-status/${id}`);
    return data;
};

export const refreshProductApi = async (id: string): Promise<ProductResponse> => {
    const { data } = await axiosInstance.put(`/products/refresh/${id}`);
    return data;
};


// 🔹 GET PRODUCTS LIST
export const getProductsApi = async (params?: {
    page?: number | undefined;
    limit?: number | undefined;
    sort?: string | undefined;
}): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.get("/products", {
        params,
    });

    return data;
};

export const getProductsAdminApi = async (params?: {
    page?: number | undefined;
    limit?: number | undefined;
    sort?: string | undefined;
}): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.get("/products/admin", {
        params,
    });
    return data;
};

// 🔹 SEARCH PRODUCTS (POST)
export const searchProductsApi = async (params: {
    q?: string;
    page?: number;
    limit?: number;
    sort?: string;
    minPrice?: number;
    maxPrice?: number;
    categoryIds?: string[];
    inStock?: boolean;
}): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.post("/products/search", params);
    return data;
};

// 🔹 GET PRODUCT BY SLUG
export const getProductBySlugApi = async (
    slug: string
): Promise<ProductResponse> => {
    const { data } = await axiosInstance.get(`/products/${slug}`);
    return data;
};

// 🔹 GET PRODUCT BY ID
export const getProductByIdApi = async (
    id: string
): Promise<ProductResponse> => {
    const { data } = await axiosInstance.get(`/products/id/${id}`);
    return data;
};
// 

// 🔹 GET SPECIAL OFFERS (singleton — returns one active offer)
export const getSpecialOffersApi = async (): Promise<SpecialOfferSingleResponse> => {
    const { data } = await axiosInstance.get("/products/special-offers");
    return data;
};

// 🔹 GET SPECIAL OFFER BY SLUG
export const getSpecialOfferBySlugApi = async (slug: string): Promise<SpecialOfferDetailResponse> => {
    const { data } = await axiosInstance.get(`/special-offer/${slug}`);
    return data;
};

// 🔹 GET NEW ARRIVALS
export const getNewArrivalsApi = async (params?: { limit?: number }): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.get("/products/new-arrivals", {
        params,
    });
    return data;
};

export const getBestSellerApi = async (params?: { limit?: number }): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.get("/products/best-sellers", {
        params,
    });
    return data;
};

// 🔹 SERVER FETCH (optional SSR)
export const getNewArrivalsServer = async () => {
    const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/products/new-arrivals`,
        { cache: "no-store" }
    );

    if (!res.ok) throw new Error("Fetch error");

    const data = await res.json();
    return data.data.data;
};

// 🔹 UPDATE PRODUCT ADMIN BY ID
export const updateProductAdminByIdApi = async (id: string, payload: FormData): Promise<ProductResponse> => {
    const { data } = await axiosInstance.put(`/products/update/${id}`, payload, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return data;
};

// 🔹 DELETE PRODUCT ADMIN BY ID
export const deleteProductAdminByIdApi = async (id: string): Promise<ProductResponse> => {
    const { data } = await axiosInstance.delete(`/products/${id}`);
    return data;
};

// 🔹 BULK UPDATE PRODUCTS
export const bulkUpdateProductsApi = async (formData: FormData): Promise<{ success: boolean; message: string }> => {
    const { data } = await axiosInstance.put("/products/bulk-update", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return data;
};


// GET DRAFT PRODUCTS
export const getDraftProductsApi = async (params?: { page?: number; limit?: number }): Promise<PaginatedProductResponse> => {
    const { data } = await axiosInstance.get("/products/admin/drafts", { params });
    return data;
};

// 🔹 SAVE PER-PRODUCT OPTION CONFIGS (price overrides + enable/disable)
export const saveOptionConfigsApi = async (
    productId: string,
    attributeOptionConfigs: Array<{ attributeOptionId: string; price: string; isEnabled: boolean }>
): Promise<{ success: boolean; data: { productId: string; saved: number } }> => {
    const { data } = await axiosInstance.patch(`/products/${productId}/option-configs`, {
        attributeOptionConfigs,
    });
    return data;
};